// QBR Admin tab — combines the narrative-prompt editor and the
// per-metric reference doc into one place. Keeps the QBR-specific
// admin surfaces away from the broad PromptsAdminTab (which is for
// PDF extraction + CSV field mapping).
//
// Two sub-panels:
//   Prompt   — view / edit / version the (migration_type='QBR',
//              stage='narrative') prompt that drives the Preview step.
//              Includes a "Load default" button that fills the editor
//              with the up-to-date base template defined below.
//   Metrics  — read-only reference for how each QBR metric is
//              computed (delegated to QbrMetricsAdminTab).

import { useEffect, useMemo, useState } from 'react';
import QbrMetricsAdminTab from './QbrMetricsAdminTab';
import {
  getActivePrompt, createPromptVersion,
  listQbrTemplates, updateQbrTemplate, setDefaultQbrTemplate, deleteQbrTemplate,
} from '../db';
import { supabase } from '../supabase';
import { PLACEHOLDER_SECTIONS } from '../qbr/placeholders';

const NAVY    = '#041662';
const ORANGE  = '#CF4A12';
const GREY    = '#6B7280';
const BORDER  = '#E5E7EB';
const CODE_BG = '#F3F4F6';
const TINT    = '#FAFBFC';

// Base template — captures everything the data + metrics pipeline now
// produces. Admins can "Load default" into the editor to reset.
//
// IMPORTANT: the {{TOKEN}} placeholders below get substituted by
// promptTemplates.interpolateTemplate() before the prompt is sent.
// They MUST appear literally in the body — describing them in prose
// ("CUSTOMER_NAME: the customer") is not enough, the interpolator
// only replaces actual {{...}} occurrences. Keep this list aligned
// with the vars block in src/qbr/narrative.js.
const DEFAULT_QBR_PROMPT = `You are drafting the narrative copy for an FMX Quarterly Business Review deck. The numbers and letter grades have already been computed from the customer's FMX site — your job is to turn them into the human-readable text the FMXer will say (and that appears on the slides).

CUSTOMER:        {{CUSTOMER_NAME}}
QUARTER:         {{QUARTER_LABEL}}

METRICS_JSON (raw numbers for the six QBR metrics):
{{METRICS_JSON}}

GRADES_JSON (A/B/C/D/F per metric):
{{GRADES_JSON}}

PRIOR_QUARTER_JSON (same shape as METRICS_JSON for the previous quarter, or null if unavailable):
{{PRIOR_QUARTER_JSON}}

ASSET_HEALTH_JSON (top equipment by WR volume this quarter; you write one recommended_action per item):
{{ASSET_HEALTH_JSON}}

METRIC SHAPES (sub-fields you may see inside METRICS_JSON):
- completion_rate: { pct }                                                — % of work-requests created in the period that were Finalized
- response_time: { responseDays, responseMeanDays, assignmentDays,
                   assignmentMeanDays, resolutionDays, resolutionMeanDays, trend } — medians + averages in days; trend = 'down'|'flat'|'up'
- planned_vs_reactive: { pct }                                            — % of completed work that was planned (PMs / total completed)
- pm_compliance: { onTimePct, anyTimePct, targetPct }                     — PM completion rates vs the customer's committed target
- backlog_aging: { overdue30dPct, oldestDays }                            — open queue health
- adoption: { active30Pct, active90Pct, mobilePct }                       — share of eligible users who logged in (mobilePct may be 0 if not entered)

TONE
Confident, specific, customer-respecting. No filler ("we are excited to share…", "leveraging synergies", etc.). Lead with outcomes, not activity. Quote actual numbers — say "your team resolved 87% of requests this quarter" rather than "high completion rate". Where a metric is weak, frame it as a coaching opportunity, not a scolding. When PRIOR_QUARTER_JSON is provided, name the direction of change in plain language ("up from 71%", "shaved two days off median resolution").

OUTPUT
Return ONLY a JSON object with this exact shape:
{
  "exec_summary": {
    "wins": ["…", "…", "…"],
    "risks": ["…", "…"]
  },
  "metric_callouts": {
    "completion_rate":     "one or two sentences",
    "response_time":       "…",
    "planned_vs_reactive": "…",
    "pm_compliance":       "…",
    "backlog_aging":       "…",
    "adoption":            "…"
  },
  "slide_captions": {
    "maintenance_activity": "one sentence under the headline number on the Maintenance Activity slide — explains the WHY (e.g. 'Driven by an increase in scheduled PM volume — not reactive breakdowns.')",
    "planned_vs_reactive":  "one sentence under the Planned vs Reactive headline (e.g. 'First quarter above 50% since onboarding.')",
    "pm_recommend":         "one or two sentences in the PM Compliance RECOMMEND box — a specific coaching action for the CSM to follow up on"
  },
  "customer_win": "one specific highlight by name (a fast resolution, a milestone, a workflow they built)",
  "asset_health": [
    { "name": "<equipment name from ASSET_HEALTH_JSON>", "recommended_action": "short imperative phrase (e.g. 'Schedule end-of-life assessment', 'Add to FY27 capital plan', 'Quarterly inspection cadence')" }
  ],
  "mutual_action_plan": [
    { "owner": "fmx" | "customer", "item": "concrete commitment for next quarter" }
  ],
  "roadmap_teaser": [
    { "title": "short title", "description": "one-sentence pitch for why this customer in particular will benefit" }
  ]
}

RULES
1. 3 wins and 1–2 risks in exec_summary. Wins should be backed by the numbers in METRICS_JSON. Don't claim a win for a metric graded C or below.
2. metric_callouts: one entry per metric. When a metric has sub-values (median + mean, on-time + any-time, 30d + 90d), name the most informative one for the customer — not all of them. Mention the grade letter only when it materially adds to the story.
3. response_time: prefer talking about *response* (first comment on the request) and *resolution* (when the work finished). Mention *assignment* only when the gap between assignment and response is notable.
4. pm_compliance: lead with on-time vs target. Mention any-time only when on-time missed the target but the team still got the work done eventually — that's a coaching story.
5. adoption: lead with the 30-day rate. If 90-day is significantly higher than 30-day, that's a sliding-engagement signal — flag it as a risk.
6. mutual_action_plan: 3–5 items, balanced between fmx and customer. Each item should be specific enough that a FMXer can hold someone to it next quarter ("Pilot the mobile work-task workflow with 5 technicians in Building A", not "Increase mobile adoption").
7. roadmap_teaser: exactly 3 items. Each is { title, description }. Tied to the metrics that scored weakest where possible.
8. slide_captions: each is one or two sentences max. Use specific numbers from METRICS_JSON. The pm_recommend caption should be actionable — name a behaviour or a follow-up, not a platitude.
9. asset_health: emit one entry per item in ASSET_HEALTH_JSON, in the same order. Each recommended_action is a short imperative phrase (under 8 words). Pick from this rough palette and adapt: "Schedule end-of-life assessment", "Add to FY27 capital plan", "PM frequency increase", "Replacement evaluation", "Quarterly inspection cadence", "Root-cause investigation". If ASSET_HEALTH_JSON is empty, return an empty array.
10. Return ONLY the JSON. No prose, no code fences.`;

export default function QbrAdminTab({ currentUserId }) {
  const [pane, setPane] = useState('prompt');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <SubTabs pane={pane} setPane={setPane} />
      {pane === 'prompt' && <QbrPromptPanel currentUserId={currentUserId} />}
      {pane === 'templates' && <QbrTemplatesPanel />}
      {pane === 'placeholders' && <QbrPlaceholdersPanel />}
      {pane === 'metrics' && (
        <div style={{ minHeight: 0, overflow: 'hidden' }}>
          <QbrMetricsAdminTab />
        </div>
      )}
    </div>
  );
}

function SubTabs({ pane, setPane }) {
  const tabs = [
    { id: 'prompt',       label: 'Narrative Prompt' },
    { id: 'templates',    label: 'Templates' },
    { id: 'placeholders', label: 'Placeholders' },
    { id: 'metrics',      label: 'Metric Reference' },
  ];
  return (
    <div style={{
      display: 'flex', gap: 2, padding: '12px 28px 0',
      borderBottom: `1px solid ${BORDER}`,
    }}>
      {tabs.map(t => {
        const active = t.id === pane;
        return (
          <button
            key={t.id}
            onClick={() => setPane(t.id)}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 600,
              border: 'none', background: 'transparent',
              borderBottom: active ? `2px solid ${ORANGE}` : '2px solid transparent',
              color: active ? NAVY : GREY,
              cursor: 'pointer', marginBottom: -1,
            }}
          >{t.label}</button>
        );
      })}
    </div>
  );
}

function QbrPromptPanel({ currentUserId }) {
  const [active, setActive] = useState(null);
  const [body, setBody] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getActivePrompt('QBR', 'narrative').then(p => {
      if (cancelled) return;
      setActive(p);
      setBody(p?.body || DEFAULT_QBR_PROMPT);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  function loadDefault() {
    if (body && body !== DEFAULT_QBR_PROMPT) {
      if (!window.confirm('Replace the editor with the default template? Unsaved changes will be lost.')) return;
    }
    setBody(DEFAULT_QBR_PROMPT);
    setNotes('Loaded default template');
  }

  async function save() {
    if (!body.trim()) { setError('Prompt body is empty.'); return; }
    setSaving(true);
    setError(null);
    const result = await createPromptVersion({
      migrationType: 'QBR',
      stage: 'narrative',
      fieldKey: null,
      body, notes: notes || null,
      makeActive: true,
      createdBy: currentUserId,
    });
    if (!result) {
      setError('Failed to save. You may not have admin permission.');
      setSaving(false);
      return;
    }
    setActive(result);
    setNotes('');
    setSavedAt(new Date().toISOString());
    setSaving(false);
  }

  const dirty = active && body !== active.body;
  const usingDefault = body === DEFAULT_QBR_PROMPT && !active;

  // Required placeholders the narrative call depends on. If the active
  // body is missing all of them, the interpolator can't inject the
  // numbers and Claude correctly responds with 'where's the data?'.
  // This banner makes that recoverable in one click.
  const REQUIRED_TOKENS = ['{{METRICS_JSON}}', '{{GRADES_JSON}}', '{{CUSTOMER_NAME}}', '{{QUARTER_LABEL}}'];
  const missingTokens = REQUIRED_TOKENS.filter(t => !body.includes(t));

  return (
    <div style={{ padding: '16px 28px', overflowY: 'auto', maxHeight: '70vh' }}>
      <p style={{ fontSize: 12, color: GREY, lineHeight: 1.55, marginTop: 0 }}>
        This prompt drives the Claude call on the QBR <strong>Preview</strong> step.
        Placeholders <code style={codeInline}>{`{{CUSTOMER_NAME}}`}</code>,{' '}
        <code style={codeInline}>{`{{QUARTER_LABEL}}`}</code>,{' '}
        <code style={codeInline}>{`{{METRICS_JSON}}`}</code>,{' '}
        <code style={codeInline}>{`{{GRADES_JSON}}`}</code>, and{' '}
        <code style={codeInline}>{`{{PRIOR_QUARTER_JSON}}`}</code> get interpolated at call time.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: GREY }}>
          {loading
            ? 'Loading…'
            : active
              ? <>Active version <strong>{active.version}</strong>{active.notes ? ` · "${active.notes}"` : ''}</>
              : <em>No active prompt yet — the editor is pre-filled with the default template.</em>}
        </div>
        <button onClick={loadDefault} disabled={loading} style={btnSubtle}>
          Load default
        </button>
      </div>

      {!loading && missingTokens.length > 0 && (
        <div style={{
          padding: '8px 12px', borderRadius: 5, fontSize: 11, marginBottom: 10,
          background: '#FFFBEB', color: '#92400E', border: '1px solid #FCD34D',
        }}>
          This prompt is missing required placeholder{missingTokens.length > 1 ? 's' : ''}:{' '}
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
            {missingTokens.join(' · ')}
          </span>
          . Without them the interpolator has nothing to inject and Claude will see only the template instructions, not the customer's actual numbers. Click <strong>Load default</strong> above to pull in a complete template, then <strong>Save new version</strong>.
        </div>
      )}

      <textarea
        value={body}
        onChange={e => setBody(e.target.value)}
        rows={20}
        spellCheck={false}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '10px 12px', fontSize: 12, lineHeight: 1.55,
          border: `1px solid ${BORDER}`, borderRadius: 6,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          outline: 'none', resize: 'vertical',
          background: '#fff',
        }}
      />

      <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: GREY, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Save notes (optional)
        </label>
        <input
          type="text" value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="What changed in this version?"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '7px 10px', fontSize: 12,
            border: `1px solid ${BORDER}`, borderRadius: 5, outline: 'none',
          }}
        />
      </div>

      {error && (
        <div style={{
          padding: '8px 12px', borderRadius: 5, fontSize: 12, marginTop: 10,
          background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA',
        }}>{error}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
        <div style={{ fontSize: 11, color: GREY }}>
          {savedAt
            ? `Saved ${new Date(savedAt).toLocaleTimeString()} — promoted to active version ${active?.version}.`
            : dirty
              ? 'Unsaved changes — save creates a new version and marks it active.'
              : usingDefault
                ? 'Default template loaded — save to make it the active prompt.'
                : 'No changes since the last active version.'}
        </div>
        <button
          onClick={save}
          disabled={saving || loading || !body.trim() || (!dirty && !usingDefault)}
          style={{
            ...btnPrimary,
            background: (saving || (!dirty && !usingDefault)) ? '#D1D5DB' : ORANGE,
            cursor: (saving || (!dirty && !usingDefault)) ? 'not-allowed' : 'pointer',
          }}
        >{saving ? 'Saving…' : 'Save new version'}</button>
      </div>
    </div>
  );
}

// Admin-managed pool of QBR .pptx templates. Admin uploads a file,
// the server scans it for {{TOKENS}} (recognized vs unrecognized
// against the placeholder registry) and saves both the file (Storage
// bucket `qbr-templates`) and a `qbr_templates` row. Two flags control
// visibility downstream:
//   is_active     — exactly one template is the default selection in
//                   the Generate-step picker. Toggling it on flips
//                   every other row off.
//   is_available  — when true, the template appears in the FMXer's
//                   picker. Lets admins keep draft/archive templates
//                   around without exposing them.
function QbrTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  async function refresh() {
    setLoading(true);
    setTemplates(await listQbrTemplates());
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function setFlag(t, patch) {
    setBusyId(t.id);
    setError(null);
    if (patch.is_active === true) {
      const updated = await setDefaultQbrTemplate(t.id);
      if (!updated) setError('Failed to set default.');
    } else {
      const updated = await updateQbrTemplate(t.id, patch);
      if (!updated) setError('Update failed.');
    }
    await refresh();
    setBusyId(null);
  }

  async function remove(t) {
    if (!window.confirm(`Delete template "${t.label || t.original_filename || t.id}"? This removes the .pptx file from Storage too.`)) return;
    setBusyId(t.id);
    setError(null);
    const ok = await deleteQbrTemplate(t.id);
    if (ok && t.storage_path) {
      await supabase.storage.from('qbr-templates').remove([t.storage_path]).catch(() => {});
    }
    if (!ok) setError('Delete failed (the qbr_templates row may have FK references in qbr_runs).');
    await refresh();
    setBusyId(null);
  }

  async function download(t) {
    if (!t.storage_path) return;
    const { data, error } = await supabase.storage
      .from('qbr-templates')
      .createSignedUrl(t.storage_path, 300); // 5 min
    if (error || !data?.signedUrl) { setError(`Couldn't sign download URL: ${error?.message || 'unknown'}`); return; }
    window.open(data.signedUrl, '_blank');
  }

  async function seedBundled({ force } = {}) {
    setSeeding(true);
    setError(null);
    try {
      const sess = (await supabase.auth.getSession()).data?.session;
      const token = sess?.access_token;
      if (!token) throw new Error('Not signed in.');
      const res = await fetch('/api/qbr-template-seed', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ force: !!force }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error || `Seed failed (${res.status}).`);
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setSeeding(false);
    }
  }

  // Whether the bundled Base FMX QBR row is already registered. Drives
  // the 'Refresh from bundle' affordance vs the cold-start CTA.
  const seededRow = templates.find(t => t.label === 'Base FMX QBR');

  return (
    <div style={{ padding: '16px 28px', overflowY: 'auto', maxHeight: '70vh' }}>
      <p style={{ fontSize: 12, color: GREY, lineHeight: 1.55, marginTop: 0 }}>
        Upload .pptx templates with <code style={codeInline}>{`{{TOKEN}}`}</code> placeholders. The deck generator
        uses the <strong>default</strong> template unless the FMXer picks an <strong>available</strong> one on
        the Generate step. The same .pptx can be uploaded with different labels for different customer
        segments (commercial QBR / small school QBR / higher-ed QBR).
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8 }}>
        <div style={{ fontSize: 12, color: GREY }}>
          {loading ? 'Loading…' : `${templates.length} template${templates.length === 1 ? '' : 's'}`}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {seededRow && (
            <button
              onClick={() => {
                if (!window.confirm("Refresh the 'Base FMX QBR' template from the bundled .pptx? Flags (default / available / notes) are preserved.")) return;
                seedBundled({ force: true });
              }}
              disabled={seeding}
              style={{ ...btnSubtle, fontWeight: 600 }}
              title="Replaces the Storage copy with the latest bundled .pptx (new tokens, renamed shapes, layout tweaks). Use after deploys that ship template changes."
            >{seeding ? 'Refreshing…' : '↻ Refresh from bundle'}</button>
          )}
          <button
            onClick={() => setUploadOpen(true)}
            style={{ ...btnSubtle, fontWeight: 600 }}
          >+ Upload new</button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '8px 12px', borderRadius: 5, fontSize: 12, marginBottom: 10,
          background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA',
        }}>{error}</div>
      )}

      {!loading && templates.length === 0 && (
        <div style={{ padding: 16, border: `1px dashed ${BORDER}`, borderRadius: 6, background: TINT }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 4 }}>
            No templates registered yet
          </div>
          <div style={{ fontSize: 12, color: GREY, marginBottom: 10 }}>
            Click below to register the bundled FMX template as the default. You can then download it, edit
            styling / static copy in PowerPoint, and re-upload via 'Upload new'.
          </div>
          <button
            onClick={seedBundled}
            disabled={seeding}
            style={{
              padding: '8px 14px', fontSize: 12, fontWeight: 700,
              background: ORANGE, color: '#fff', border: 'none', borderRadius: 5,
              cursor: seeding ? 'wait' : 'pointer',
            }}
          >{seeding ? 'Seeding…' : 'Seed bundled FMX template'}</button>
        </div>
      )}

      {templates.map(t => {
        const recognized   = t.placeholder_map?.recognized || [];
        const unrecognized = t.placeholder_map?.unrecognized || [];
        return (
          <div key={t.id} style={{
            border: `1px solid ${BORDER}`, borderRadius: 8, padding: 12, marginBottom: 10, background: '#fff',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                  {t.label || t.original_filename || '(untitled)'}
                  {t.is_active && <span style={badgeDefault}>DEFAULT</span>}
                  {t.is_available && <span style={badgeAvailable}>AVAILABLE</span>}
                </div>
                <div style={{ fontSize: 11, color: GREY, marginTop: 2 }}>
                  {t.original_filename ? <>{t.original_filename} · </> : null}
                  Uploaded {new Date(t.uploaded_at).toLocaleString()}
                </div>
                {t.notes && <div style={{ fontSize: 11, color: '#374151', marginTop: 4 }}>{t.notes}</div>}
                <div style={{ fontSize: 11, color: GREY, marginTop: 6 }}>
                  {recognized.length} recognized, {unrecognized.length} unrecognized {unrecognized.length > 0 && (
                    <span style={{ color: '#92400E' }}>
                      ({unrecognized.slice(0, 4).join(', ')}{unrecognized.length > 4 ? '…' : ''})
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!t.is_active} disabled={busyId === t.id}
                    onChange={e => setFlag(t, { is_active: e.target.checked })} /> Default
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!t.is_available} disabled={busyId === t.id}
                    onChange={e => setFlag(t, { is_available: e.target.checked })} /> Available to FMXer
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={() => download(t)} style={btnSubtle}>Download</button>
              <button onClick={() => remove(t)} style={{ ...btnSubtle, color: '#B91C1C' }} disabled={busyId === t.id}>Delete</button>
            </div>
          </div>
        );
      })}

      {uploadOpen && (
        <TemplateUploadModal
          onCancel={() => setUploadOpen(false)}
          onUploaded={async () => { setUploadOpen(false); await refresh(); }}
        />
      )}
    </div>
  );
}

function TemplateUploadModal({ onCancel, onUploaded }) {
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function upload() {
    if (!file) { setError('Pick a .pptx file first.'); return; }
    setUploading(true);
    setError(null);
    try {
      const base64 = await fileToBase64(file);
      const sess = (await supabase.auth.getSession()).data?.session;
      const token = sess?.access_token;
      if (!token) throw new Error('Not signed in.');
      const res = await fetch('/api/qbr-template-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          fileName: file.name,
          fileBase64: base64,
          label: label.trim() || null,
          notes: notes.trim() || null,
          isAvailable, isDefault,
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error || `Upload failed (${res.status}).`);
      setResult(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={modalOverlayStyle}>
      <div style={modalCardStyle}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>Upload QBR template</div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 20, color: GREY, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 18, display: 'grid', gap: 10 }}>
          {!result ? (
            <>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: 11, color: GREY, fontWeight: 600 }}>.pptx file</span>
                <input type="file" accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  onChange={e => setFile(e.target.files?.[0] || null)} />
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: 11, color: GREY, fontWeight: 600 }}>Label (FMXer sees this in the picker)</span>
                <input type="text" value={label} onChange={e => setLabel(e.target.value)}
                  placeholder="e.g. Commercial QBR" style={inputStyle} />
              </label>
              <label style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: 11, color: GREY, fontWeight: 600 }}>Notes (admin-only)</span>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={isAvailable} onChange={e => setIsAvailable(e.target.checked)} />
                Make available to FMXer
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
                Set as default (replaces any current default)
              </label>
              {error && (
                <div style={{ padding: '8px 12px', borderRadius: 5, fontSize: 12, background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}>
                  {error}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 }}>
                <button onClick={onCancel} style={btnSubtle} disabled={uploading}>Cancel</button>
                <button onClick={upload} disabled={uploading || !file}
                  style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, background: ORANGE, color: '#fff', border: 'none', borderRadius: 5, cursor: (uploading || !file) ? 'not-allowed' : 'pointer' }}>
                  {uploading ? 'Uploading + scanning…' : 'Upload'}
                </button>
              </div>
            </>
          ) : (
            <UploadResultView result={result} onClose={onUploaded} />
          )}
        </div>
      </div>
    </div>
  );
}

function UploadResultView({ result, onClose }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1A7F4E' }}>
        Uploaded — {result.slidesScanned} slide{result.slidesScanned === 1 ? '' : 's'} scanned.
      </div>
      <div style={{ fontSize: 12 }}>
        <strong>{result.recognized.length}</strong> recognized token{result.recognized.length === 1 ? '' : 's'},{' '}
        <strong>{result.unrecognized.length}</strong> unrecognized.
      </div>
      {result.unrecognized.length > 0 && (
        <div style={{ padding: '8px 12px', borderRadius: 5, fontSize: 11, background: '#FFFBEB', color: '#92400E', border: '1px solid #FCD34D' }}>
          <strong>Unrecognized tokens</strong> — these render as em-dash at generate time. Typo, or new tokens that need to be added to the registry?
          <div style={{ marginTop: 6, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
            {result.unrecognized.join(', ')}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <button onClick={onClose} style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, background: NAVY, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}>
          Done
        </button>
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

const badgeDefault = {
  marginLeft: 8, padding: '1px 6px', fontSize: 9, fontWeight: 700,
  borderRadius: 8, background: ORANGE, color: '#fff', letterSpacing: 0.3,
};
const badgeAvailable = {
  marginLeft: 6, padding: '1px 6px', fontSize: 9, fontWeight: 700,
  borderRadius: 8, background: NAVY, color: '#fff', letterSpacing: 0.3,
};
const inputStyle = {
  padding: '6px 9px', fontSize: 12, borderRadius: 5,
  border: `1px solid ${BORDER}`, outline: 'none',
  width: '100%', boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const modalOverlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20,
};
const modalCardStyle = {
  background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column',
};

// Read-only reference of every {{TOKEN}} the deck builder knows how to
// substitute. Grouped by slide; a search box at the top filters by
// token name OR description. The intent is: an admin editing the
// .pptx template can come here, search 'response', and copy the right
// strings into their slide layout.
function QbrPlaceholdersPanel() {
  const [query, setQuery] = useState('');
  const lowerQ = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!lowerQ) return PLACEHOLDER_SECTIONS;
    return PLACEHOLDER_SECTIONS
      .map(section => ({
        ...section,
        tokens: section.tokens.filter(t =>
          t.token.toLowerCase().includes(lowerQ) ||
          (t.description || '').toLowerCase().includes(lowerQ) ||
          (t.source      || '').toLowerCase().includes(lowerQ)
        ),
      }))
      .filter(s => s.tokens.length > 0);
  }, [lowerQ]);

  const total = PLACEHOLDER_SECTIONS.reduce((a, s) => a + s.tokens.length, 0);

  function copyToken(name) {
    const text = `{{${name}}}`;
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).catch(() => {});
  }

  return (
    <div style={{ padding: '16px 28px', overflowY: 'auto', maxHeight: '70vh' }}>
      <p style={{ fontSize: 12, color: GREY, lineHeight: 1.55, marginTop: 0 }}>
        Every <code style={codeInline}>{`{{TOKEN}}`}</code> the deck builder fills in. Use this list while editing
        the QBR PowerPoint template — drop the exact string anywhere a value should land. Click a token to copy
        it. Tokens left unfilled at generate time render as an em-dash so a forgotten placeholder is obvious.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search tokens or descriptions…"
          style={{
            flex: 1, padding: '7px 10px', fontSize: 12,
            border: `1px solid ${BORDER}`, borderRadius: 5, outline: 'none',
          }}
        />
        <div style={{ fontSize: 11, color: GREY }}>
          {lowerQ
            ? `${filtered.reduce((a, s) => a + s.tokens.length, 0)} of ${total} match`
            : `${total} tokens total`}
        </div>
      </div>

      {filtered.length === 0 && (
        <div style={{ fontSize: 12, color: GREY, fontStyle: 'italic', padding: 16, textAlign: 'center' }}>
          No tokens match "{query}".
        </div>
      )}

      {filtered.map(section => (
        <div key={section.label} style={{
          border: `1px solid ${BORDER}`, borderRadius: 8,
          marginBottom: 12, background: '#fff', overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 14px', borderBottom: `1px solid ${BORDER}`,
            background: TINT, display: 'flex', alignItems: 'baseline', gap: 10, justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: NAVY }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{section.label}</div>
            </div>
            <div style={{ fontSize: 10, color: GREY, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
              {section.tokens.length} token{section.tokens.length === 1 ? '' : 's'}
            </div>
          </div>
          {section.notes && (
            <div style={{ padding: '8px 14px', fontSize: 11, color: GREY, fontStyle: 'italic', borderBottom: `1px dashed ${BORDER}` }}>
              {section.notes}
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: GREY, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                <th style={thStyle}>Token</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Example</th>
              </tr>
            </thead>
            <tbody>
              {section.tokens.map(t => (
                <tr key={t.token} style={{ borderTop: `1px solid ${BORDER}` }}>
                  <td style={{ ...tdStyle, padding: 0, verticalAlign: 'top' }}>
                    <button
                      onClick={() => copyToken(t.token)}
                      title="Copy as {{TOKEN}}"
                      style={{
                        ...codeInline,
                        padding: '4px 8px', margin: 8,
                        cursor: 'pointer', border: `1px solid ${BORDER}`,
                        background: '#fff',
                      }}
                    >{`{{${t.token}}}`}</button>
                  </td>
                  <td style={tdStyle}>{t.description}</td>
                  <td style={{ ...tdStyle, color: GREY }}>{t.source || '—'}</td>
                  <td style={{ ...tdStyle, color: GREY }}>{t.example || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

const thStyle = {
  textAlign: 'left', padding: '8px 14px',
  background: TINT, borderBottom: `1px solid ${BORDER}`,
};
const tdStyle = {
  padding: '8px 14px', verticalAlign: 'top',
  fontSize: 12, color: '#374151', lineHeight: 1.5,
};

const codeInline = {
  fontSize: 11, padding: '1px 5px', borderRadius: 3,
  background: CODE_BG, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
};
const btnSubtle = {
  padding: '6px 12px', fontSize: 11, fontWeight: 600,
  background: '#fff', color: NAVY,
  border: `1px solid ${BORDER}`, borderRadius: 5, cursor: 'pointer',
};
const btnPrimary = {
  padding: '8px 18px', fontSize: 12, fontWeight: 600,
  color: '#fff', border: 'none', borderRadius: 5,
};
